import { AxiosResponse } from "axios";
import fileDownload from "js-file-download";
import $ from 'jquery';

/**
 * Get filename from File response object
 * @param response The axios response
 */
export const getFileName = (
    response: AxiosResponse
) => {
    const disposition = response.request.getResponseHeader('Content-Disposition')
    var fileName = "";
    var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    var matches = filenameRegex.exec(disposition);
    if (matches != null && matches[1]) {
        fileName = matches[1].replace(/['"]/g, '');
    }

    return fileName;
};

/**
 * Download the file by creating a temp link attached to the body. NB: The responseType from the original request must be `blob` or 'arraybuffer'
 * @param response The axios response
 * @param fileName The file name
 */
export const downloadFile = (
    response: AxiosResponse,
    fileName?: string
) => {
    if (!["blob", "arraybuffer"].includes(response.request?.responseType)) {
        throw "Could not download file. Axios `responseType` must be set to 'blob' or 'arraybuffer'."
    }
    fileName = fileName || getFileName(response);
    fileDownload(response.data, fileName)
};

/**
 * Add or remove a class from an element
 * @param element The element to change
 * @param className The class name to be added or removed
 */
export const toggleClass = (
    element: HTMLElement | null | undefined,
    className: string
) => {
    if (!element) return;
    if (element.classList.contains(className)) {
        element.classList.remove(className);
    } else {
        element.classList.add(className);
    }
};

/**
 * Update the state of a "select all" checkbox based on the related checkboxes
 *
 * @param selectAllCheckbox The select all checkbox
 * @param relatedCheckboxes The checkboxes that link to the select all checkbox
 */
export const updateSelectAllCheckboxState = (
    selectAllCheckbox: HTMLInputElement | undefined | null,
    relatedCheckboxes: NodeListOf<HTMLInputElement> | HTMLInputElement[]
) => {
    if (!selectAllCheckbox) {
        return;
    }

    let areAllChecked = true;
    relatedCheckboxes.forEach((checkbox: HTMLInputElement) => {
        if (!checkbox.checked) {
            areAllChecked = false;
        }
    });
    selectAllCheckbox.checked = areAllChecked;
};

/**
 * Update the state of a list of checkboxes to match the state of a "select all" checkbox
 *
 * @param selectAllCheckbox The select all checkbox
 * @param relatedCheckboxes The checkboxes that link to the select all checkbox
 */
export const updateCheckboxesToMatchSelectAll = (
    selectAllCheckbox: HTMLInputElement | undefined | null,
    relatedCheckboxes: NodeListOf<HTMLInputElement> | HTMLInputElement[]
) => {
    if (!selectAllCheckbox) {
        return;
    }
    const isSelectAllCheckboxChecked = selectAllCheckbox.checked;
    relatedCheckboxes.forEach((checkbox: HTMLInputElement) => {
        checkbox.checked = isSelectAllCheckboxChecked;
    });
};

/**
 * Add event listeners to a select-all checkbox and a list of related checkboxes.
 * When the select-all checkbox is checked, all related checkboxes will be selected and vice-versa.
 *
 * @param selectAllCheckbox The select all checkbox
 * @param relatedCheckboxes The checkboxes that link to the select all checkbox
 */
export const bindSelectAllCheckboxToList = (
    selectAllCheckbox: HTMLInputElement | undefined | null,
    relatedCheckboxes: NodeListOf<HTMLInputElement>
) => {
    var activeCheckboxes: Array<HTMLInputElement> = new Array<HTMLInputElement>();

    // Get only non disabled checkboxes
    relatedCheckboxes.forEach(cb => {
        if (!cb.disabled) {
            activeCheckboxes.push(cb);
        };
    });

    if (!selectAllCheckbox) {
        console.warn("Could not find select-all checkbox to bind to list", { selectAllCheckbox, activeCheckboxes });
        return;
    }
    if (activeCheckboxes.length === 0) {
        console.warn("No related checkboxes found to bind select-all checkbox to", { selectAllCheckbox, activeCheckboxes });
        return;
    }

    activeCheckboxes.forEach(cb => {
        // On click of a related checkbox, update the state of the select-all checkbox
        cb.addEventListener("click", () => {
            updateSelectAllCheckboxState(selectAllCheckbox, activeCheckboxes);
        });
    });

    selectAllCheckbox.addEventListener("change", () => {
        // On click of the select-all checkbox, either select or de-select all checkboxes
        updateCheckboxesToMatchSelectAll(selectAllCheckbox, activeCheckboxes);
    });
}

/**
 * An element query selector string or a single Element
 */
export type SelectorOrElement = string | Element | null | undefined;

/**
 * An element query selector string or a NodeList<T> or elements
 */
export type SelectorOrElementList =
    | string
    | NodeListOf<Element>
    | null
    | undefined;

/**
 * Convert a query selector string or Element into an Element of <T>
 *
 * @param selectorOrElement Query selector string or the reference to a HTML node
 * @param parent The parent node to use for the query selector`. Defaults to the HTML document
 */
export function convertToElement<T extends Element>(
    selectorOrElement: SelectorOrElement,
    parent: Element | Document = document
): T | undefined {
    return typeof selectorOrElement === "string"
        ? parent.querySelector<T>(selectorOrElement) ?? undefined
        : (selectorOrElement as T);
}

/**
 * Convert a query selector string or NodeList<any> into a NodeList<T>
 *
 * @param selectorOrElement Query selector string or the reference to a node list of HTML nodes
 * @param parent The parent node to use for the query selector`. Defaults to the HTML document
 */
export function convertToElementList<T extends Element>(
    selectorOrElement: SelectorOrElementList,
    parent: Element | Document = document
): NodeListOf<T> {
    return typeof selectorOrElement === "string"
        ? parent.querySelectorAll<T>(selectorOrElement)
        : (selectorOrElement as NodeListOf<T>);
}


/**
 * Retrieve a HTML element if it exists. Throw an error otherwise
 *
 * @param parentNode The element to make the query relative to
 * @param selector The selector to use for querying the element
 * @param elementName The human-readable name of the element that you're querying. e.g "upload form" or "submit button"
 * @returns The requested element if found. If not found, an error will be thrown
 */
export function getElement<TElementType extends HTMLElement>(parentNode: Element | Document, selector: string, elementName = 'element'): TElementType {
    const element = document.querySelector<TElementType>(selector);
    if (!element) {
        const errorMessage = `Could not find ${elementName} with selector: '${selector}'.`;
        console.error(errorMessage, {
            parentNode,
            selector,
            elementName
        })
        throw new Error(errorMessage)
    }
    return element;
}


/**
 * Sleep for a given period of time. Async alternative to `setTimeout()`
 *
 * @param sleepTimeMs The amount of time to sleep for, in MS. E.g 1000 = 1 second
 * @example
 *  console.log("Hello...")
 *  await sleep(1000);
 *  console.log("...world!")
 */
export function sleep(sleepTimeMs: number) {
    return new Promise<void>((res, rej) => {
        setTimeout(() => {
            res();
        }, sleepTimeMs)
    })
}

/**
 * Bind select all checkbox to the relevant checkboxes
 * Keep track of which files have been selected
 * @param selectAll
 * @param checkboxes
 * @param selectedFiles
 */
export function bindSelectAllCheckboxToPagedList(selectAll: JQuery<HTMLElement>, checkboxes: JQuery<HTMLElement>, selectedFiles: string[]) {

    if (!selectAll) {
        console.warn("Could not find select all checkbox to bind the list of relevant checkboxes", { selectAll, checkboxes })
    }

    if (!checkboxes) {
        console.warn("Could not find relevant checkboxes to bind select all", { selectAll, checkboxes })
        return;
    }

    // Change state of relevant checkboxes depending on the select all checkbox
    // Add/remove files to the list of selected files accordingly
    selectAll.on("change", () => {
        const isSelectAll = selectAll.is(":checked");

        if (isSelectAll) {
            checkboxes.each((_, cb) => {
                let filename = $(cb)?.val()?.toString();
                let isCheckboxEnabled = !$(cb).is(':disabled');

                if (filename && isCheckboxEnabled) {
                    let indexOfFile = selectedFiles.indexOf(filename);

                    if (indexOfFile === -1) {
                        selectedFiles.push(filename);
                    }
                }

                if (isCheckboxEnabled) {
                    $(cb).prop('checked', isSelectAll);
                }
            })
        } else {
            checkboxes.each((_, cb) => {
                let filename = $(cb)?.val()?.toString();
                let isCheckboxEnabled = !$(cb).is(':disabled');

                if (filename && isCheckboxEnabled) {
                    let indexOfFile = selectedFiles.indexOf(filename);

                    if (indexOfFile >= 0) {
                        selectedFiles.splice(indexOfFile, 1);
                    }
                }

                if (isCheckboxEnabled) {
                    $(cb).prop('checked', isSelectAll);
                }
            })

        }
    })

    // Change state of select all checkbox depending on the state of relevant checkboxes
    checkboxes.each((_, cb) => {
        let areAllChecked = true;
        checkboxes.each((_, cb) => {
            let isCheckboxEnabled = !$(cb).is(":disabled");

            if (!$(cb).is(":checked") && isCheckboxEnabled) {
                areAllChecked = false;
            }

        });
        $(selectAll).prop('checked', areAllChecked);

        // Keep track of the state of each relevant checkbox and update the select all checkbox accordingly
        // Add/remove files from the list of selected files accordingly
        $(cb).on("change", () => {
            let areAllChecked = true;
            let filename = $(cb)?.val()?.toString();
            let indexOfFile = -1;
            let isCheckboxChecked = $(cb).is(":checked");
            let isCheckboxEnabled = !$(cb).is(":disabled");

            if (filename && isCheckboxEnabled) {
                indexOfFile = selectedFiles.indexOf(filename);

                if (isCheckboxChecked && indexOfFile === -1) {
                    selectedFiles.push(filename);
                } else if (indexOfFile >= 0 && !isCheckboxChecked) {
                    selectedFiles.splice(indexOfFile, 1);
                }
            }

            checkboxes.each((_, cb) => {
                let isCheckboxChecked = $(cb).is(":checked");
                let isCheckboxEnabled = !$(cb).is(':disabled');

                if (!isCheckboxChecked && isCheckboxEnabled) {
                    areAllChecked = false;
                }
            })
            $(selectAll).prop('checked', areAllChecked);
        })
    })

    let disabledCheckboxes: HTMLElement[] = [];

    checkboxes.each((_, cb) => {
        let isCheckboxDisabled = $(cb).is(':disabled');

        if (isCheckboxDisabled) {
            disabledCheckboxes.push(cb);
        }
    })

    // If checkboxes in currently viewed page in table are all disabled, ensure select all is unticked on inital load
    if (checkboxes.length === disabledCheckboxes.length) {
        $(selectAll).prop('checked', false);
    }
}
